import React, { useState, useEffect } from 'react';
import { DollarSign, Calculator, Send, CheckCircle, Clock, XCircle, ArrowLeft } from 'lucide-react';
import axios from 'axios';
import MentorLayout from '@/components/mentor/MentorLayout';

import { BACKEND_URL } from '../../config/api';
const RefundManagement = ({ user, onLogout }) => {
  const [view, setView] = useState('list'); // 'list' or 'calculate'
  const [refundRequests, setRefundRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [allStudents, setAllStudents] = useState([]);

  // Calculator form state
  const [calculatorForm, setCalculatorForm] = useState({
    student_id: '',
    days_used: '',
    total_days: '',
    fees_paid: '',
    reason: 'technical',
    mentor_notes: ''
  });

  const [calculationResult, setCalculationResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchStudents();
  }, []);

  const fetchStudents = async () => {
    try {
      const token = localStorage.getItem('lms_token');
      const response = await axios.get(
        `${BACKEND_URL}/api/mentor/students`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAllStudents(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching students:', error);
      setLoading(false);
    }
  };

  const calculateRefund = async () => {
    try {
      const token = localStorage.getItem('lms_token');
      const response = await axios.post(
        `${BACKEND_URL}/api/mentor/refund/calculate?student_id=${calculatorForm.student_id}&days_used=${calculatorForm.days_used}&total_days=${calculatorForm.total_days}&fees_paid=${calculatorForm.fees_paid}&reason=${calculatorForm.reason}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCalculationResult(response.data);
    } catch (error) {
      console.error('Error calculating refund:', error);
      alert('Failed to calculate refund');
    }
  };

  const submitRefundRequest = async () => {
    if (!calculationResult) {
      alert('Please calculate refund first');
      return;
    }

    if (!calculatorForm.mentor_notes.trim()) {
      alert('Please add mentor notes');
      return;
    }

    try {
      setSubmitting(true);
      const token = localStorage.getItem('lms_token');
      await axios.post(
        `${BACKEND_URL}/api/mentor/refund/submit?student_id=${calculatorForm.student_id}&calculated_amount=${calculationResult.calculated_refund}&reason=${calculatorForm.reason}&mentor_notes=${encodeURIComponent(calculatorForm.mentor_notes)}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      alert('Refund request submitted successfully!');
      resetForm();
      setView('list');
    } catch (error) {
      console.error('Error submitting refund:', error);
      alert(error.response?.data?.detail || 'Failed to submit refund request');
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setCalculatorForm({
      student_id: '',
      days_used: '',
      total_days: '',
      fees_paid: '',
      reason: 'technical',
      mentor_notes: ''
    });
    setCalculationResult(null);
    setSelectedStudent(null);
  };

  const selectStudent = (student) => {
    setSelectedStudent(student);
    setCalculatorForm({
      ...calculatorForm,
      student_id: student.id,
      fees_paid: student.outstanding_fee > 0 ? '' : '50000' // Default to 50k if fully paid
    });
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/3"></div>
          <div className="h-32 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <MentorLayout user={user} onLogout={onLogout}>
      <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
            <DollarSign className="w-8 h-8 text-green-600" />
            Refund Management
          </h1>
          <p className="text-gray-600 mt-2">
            Calculate and submit refund requests for super admin approval
          </p>
        </div>

        {view === 'calculate' ? (
          <button
            onClick={() => {
              resetForm();
              setView('list');
            }}
            className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            Back to List
          </button>
        ) : (
          <button
            onClick={() => setView('calculate')}
            className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors shadow-md"
          >
            <Calculator className="w-5 h-5" />
            New Refund Request
          </button>
        )}
      </div>

      {view === 'calculate' ? (
        /* Calculator View */
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-6">Refund Calculator</h2>

          {/* Student Selection */}
          {!selectedStudent ? (
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Student
              </label>
              <select
                onChange={(e) => {
                  const student = allStudents.find(s => s.id === e.target.value);
                  if (student) selectStudent(student);
                }}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Choose a student...</option>
                {allStudents.map(student => (
                  <option key={student.id} value={student.id}>
                    {student.name} - {student.email}
                  </option>
                ))}
              </select>
            </div>
          ) : (
            <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-semibold text-gray-900">{selectedStudent.name}</p>
                  <p className="text-sm text-gray-600">{selectedStudent.email}</p>
                </div>
                <button
                  onClick={() => {
                    setSelectedStudent(null);
                    setCalculatorForm({ ...calculatorForm, student_id: '' });
                    setCalculationResult(null);
                  }}
                  className="text-sm text-blue-600 hover:text-blue-700"
                >
                  Change Student
                </button>
              </div>
            </div>
          )}

          {selectedStudent && (
            <>
              {/* Input Form */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Days Used
                  </label>
                  <input
                    type="number"
                    value={calculatorForm.days_used}
                    onChange={(e) =>
                      setCalculatorForm({ ...calculatorForm, days_used: e.target.value })
                    }
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., 30"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Total Days (Course Duration)
                  </label>
                  <input
                    type="number"
                    value={calculatorForm.total_days}
                    onChange={(e) =>
                      setCalculatorForm({ ...calculatorForm, total_days: e.target.value })
                    }
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., 365"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Fees Paid (₹)
                  </label>
                  <input
                    type="number"
                    value={calculatorForm.fees_paid}
                    onChange={(e) =>
                      setCalculatorForm({ ...calculatorForm, fees_paid: e.target.value })
                    }
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., 50000"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Refund Reason
                  </label>
                  <select
                    value={calculatorForm.reason}
                    onChange={(e) =>
                      setCalculatorForm({ ...calculatorForm, reason: e.target.value })
                    }
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="technical">Technical Issues (100% refund)</option>
                    <option value="dissatisfied">Dissatisfied (90% refund)</option>
                    <option value="financial">Financial Hardship (80% refund)</option>
                  </select>
                </div>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Mentor Notes *
                </label>
                <textarea
                  value={calculatorForm.mentor_notes}
                  onChange={(e) =>
                    setCalculatorForm({ ...calculatorForm, mentor_notes: e.target.value })
                  }
                  rows={4}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Provide context for this refund request..."
                />
              </div>

              {/* Calculate Button */}
              <button
                onClick={calculateRefund}
                disabled={
                  !calculatorForm.days_used ||
                  !calculatorForm.total_days ||
                  !calculatorForm.fees_paid
                }
                className="w-full mb-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors font-semibold flex items-center justify-center gap-2"
              >
                <Calculator className="w-5 h-5" />
                Calculate Refund
              </button>

              {/* Calculation Result */}
              {calculationResult && (
                <div className="bg-gradient-to-br from-green-50 to-blue-50 border-2 border-green-300 rounded-lg p-6 mb-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-4">Calculation Result</h3>

                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div className="bg-white p-4 rounded-lg">
                      <p className="text-sm text-gray-600">Fees Paid</p>
                      <p className="text-2xl font-bold text-gray-900">
                        ₹{calculationResult.fees_paid.toLocaleString()}
                      </p>
                    </div>
                    <div className="bg-white p-4 rounded-lg">
                      <p className="text-sm text-gray-600">Usage</p>
                      <p className="text-2xl font-bold text-gray-900">
                        {calculationResult.days_used}/{calculationResult.total_days} days
                      </p>
                    </div>
                    <div className="bg-white p-4 rounded-lg">
                      <p className="text-sm text-gray-600">Refund %</p>
                      <p className="text-2xl font-bold text-blue-600">
                        {calculationResult.refund_percentage}%
                      </p>
                    </div>
                    <div className="bg-white p-4 rounded-lg">
                      <p className="text-sm text-gray-600">Adjustment Factor</p>
                      <p className="text-2xl font-bold text-orange-600">
                        {(calculationResult.adjustment_factor * 100).toFixed(0)}%
                      </p>
                    </div>
                  </div>

                  <div className="bg-green-600 text-white p-6 rounded-lg text-center">
                    <p className="text-sm mb-2">Final Refund Amount</p>
                    <p className="text-4xl font-bold">
                      ₹{calculationResult.calculated_refund.toLocaleString()}
                    </p>
                  </div>
                </div>
              )}

              {/* Submit Button */}
              {calculationResult && (
                <button
                  onClick={submitRefundRequest}
                  disabled={submitting || !calculatorForm.mentor_notes.trim()}
                  className="w-full py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors font-semibold flex items-center justify-center gap-2"
                >
                  <Send className="w-5 h-5" />
                  {submitting ? 'Submitting...' : 'Submit to Super Admin'}
                </button>
              )}
            </>
          )}
        </div>
      ) : (
        /* List View */
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-6">Recent Refund Requests</h2>
          <div className="text-center py-12 text-gray-500">
            <Clock className="w-16 h-16 mx-auto mb-4 text-gray-300" />
            <p>No refund requests yet</p>
            <p className="text-sm mt-2">Click "New Refund Request" to create one</p>
          </div>
        </div>
      )}
    </div>
    </MentorLayout>
  );
};

export default RefundManagement;
